import React, { useEffect, useState } from 'react'
import { LuFileText, LuPhone } from 'react-icons/lu';
import { RiUserSharedLine } from "react-icons/ri";
import { BiUserCheck } from "react-icons/bi";
import { PiVideoLight } from "react-icons/pi";
import { FaChevronLeft } from "react-icons/fa";
import { getHiringStages, setupAutomations, generatePreScreeningQuestions, fetchJobById } from '../../Redux/ActionCreators/jobsAction';
import { updateHiringAssistaniData } from '../../Redux/ActionCreators/qualifyQuestionsAction';
import { getSubscriptionPlans } from '../../Redux/ActionCreators/userAction';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import PreScreening from '../JobScreening/preScreening';
import TeleScreening from '../JobScreening/teleScreening';
import { Snackbar, Alert } from '@mui/material';

const FEATURE_MAPPING = {
    "Resume Scoring": {
        description: 'AI-powered ranking of candidates based on job description alignment.',
        icon: <LuFileText size={40} />,
        defaultSelected: true,
    },
    "Pre Screening": {
        description: `Custom qualifying questions to filter candidates before the first interview.`,
        icon: <RiUserSharedLine size={40} />,
        defaultSelected: true,
    },
    "AI Telephonic Screening": {
        description: 'Automated human-like AI-powered first-round voice screening with job-fit analysis.',
        icon: <LuPhone size={40} />,
        defaultSelected: true,
    },
    "AI Candidate Assessments": {
        description: 'Skill-based technical and behavioral tests tailored to the role.',
        icon: <BiUserCheck size={40} />,
        defaultSelected: false,
    },
    "AI Video Interviews": {
        description: '30-minute in-depth AI video interview with flexible scheduling and measurable insights',
        icon: <PiVideoLight size={40} />,
        defaultSelected: false,
    },
};

const ORDERED_NAMES = [
    { name: "Resume Scoring", id: "cf1bb4ea-355b-4861-ad7b-ba52d3ce6b82" },
    { name: "Pre Screening", id: "3dab71cf-c680-4983-a109-1cff0953999b" },
    { name: "AI Telephonic Screening", id: "fd40e3d2-38a9-4cef-937a-93ef6a42d045" },
    { name: "AI Candidate Assessments", id: "293e9b95-4610-48f2-ae93-c7264cae7d6a" },
    { name: "AI Video Interviews", id: "ed658e02-1549-4d9e-a735-1f1c1e385f46" }
];

function Stage2SelectAIFeatures({ jobId, onNext, onBack, transitioning, transitionMessage, onTransitionStart, onTransitionError }) {
    const dispatch = useDispatch();
    const [selected, setSelected] = useState({})
    const [loadingStages, setLoadingStages] = useState(false);

    const [activeSettingsView, setActiveSettingsView] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [lockedModal, setLockedModal] = useState(false);
    const [generatingPreScreening, setGeneratingPreScreening] = useState(false);
    const hasGeneratedPreScreening = React.useRef(false);

    const { hiringStages } = useSelector(state => state.jobs);
    const { selectedTenant } = useSelector((state) => state.users);

    useEffect(() => {
        const fetchStages = async () => {
            if (!jobId || transitioning) return;
            const tenantId = localStorage.getItem("tenentId");
            setLoadingStages(true);
            await dispatch(getSubscriptionPlans(tenantId));
            await dispatch(getHiringStages(jobId));
            setLoadingStages(false);
        };
        fetchStages();
    }, [dispatch, jobId, transitioning]);

    // Initialize selected state when hiringStages loads
    useEffect(() => {
        if (hiringStages && hiringStages.length > 0) {
            const initialSelected = {};
            hiringStages.forEach(stage => {
                if (stage.is_locked) {
                    initialSelected[stage.hiring_stage_id] = false;
                } else {
                    initialSelected[stage.hiring_stage_id] = !!stage.is_enabled;
                }
            });
            setSelected(initialSelected);
        }
    }, [hiringStages]);

    // After stages load, generate pre-screening questions once
    useEffect(() => {
        if (!jobId || transitioning || !hiringStages?.length || hasGeneratedPreScreening.current) return;
        hasGeneratedPreScreening.current = true;

        const runGenerate = async () => {
            setGeneratingPreScreening(true);
            const job = await dispatch(fetchJobById(jobId));
            if (job) {
                const body = {
                    job_title: job.title || '',
                    company_name: job.company_name || '',
                    job_type: job.job_type?.name || job.job_type || '',
                    skills_required: Array.isArray(job.skills) ? job.skills.map(s => s.name || s) : [],
                    job_location_iso: job.country?.iso2 || '',
                    job_location_text: [job.city?.name, job.country?.name].filter(Boolean).join(', '),
                    job_location_city: job.city?.name || '',
                    job_location_country: job.country?.name || '',
                    experience_required: job.experience_required || '',
                    job_salary: job.salary || '',
                    job_qualification: job.qualifications || '',
                    job_description: job.description || '',
                };
                await dispatch(generatePreScreeningQuestions(jobId, body));
            }
            setGeneratingPreScreening(false);
        };
        runGenerate();
    }, [jobId, transitioning, hiringStages, dispatch]);

    const toggleFeature = async (stage) => {
        if (stage.is_locked) {
            setLockedModal(true);
            return;
        }
        const uiKey = stage.hiring_stage_id;
        const payloadKey = stage.id;
        const currentState = !!selected[uiKey];
        const nextState = !currentState;

        // Enforce at least one selection
        if (!nextState) {
            const hasChecked = Object.keys(selected).some(k => k !== uiKey && selected[k] === true);
            if (!hasChecked) {
                return; // Revert change if it leaves 0 selected
            }
        }

        setLoadingStages(true);
        try {
            const resp = await dispatch(updateHiringAssistaniData(jobId, payloadKey, { is_enabled: nextState }));
            if (resp && resp.success !== false) {
                await dispatch(getHiringStages(jobId));
            } else {
                setSnackbar({ open: true, message: resp?.message || 'Failed to update feature state' });
            }
        } catch (error) {
            console.error("Failed to update feature:", error);
            setSnackbar({ open: true, message: 'Failed to update feature state' });
        } finally {
            setLoadingStages(false);
        }
    }

    const handleEditClick = (e, stageName) => {
        e.stopPropagation();
        if (stageName === 'Pre Screening' || stageName === 'Voice Call') {
            setActiveSettingsView(stageName);
        }
    }

    const FEATURE_NAME_TO_USAGE_NAME = {
        "AI Telephonic Screening": "Voice Call",
        "AI Video Interviews": "Video Call",
        "Resume Scoring": "Resume Scoring",
        "Pre Screening": "Prescreening",
        "AI Candidate Assessments": "Assessment"
    };

    const getRemainingCredits = (featureName) => {
        const usages = selectedTenant?.data?.feature_usages || selectedTenant?.feature_usages || [];
        const usageName = FEATURE_NAME_TO_USAGE_NAME[featureName];
        if (!usageName) return null;
        const usage = usages.find(u => u.subscription_feature?.name === usageName);
        return usage && usage.remaining_count !== undefined ? usage.remaining_count : null;
    };

    // Filter and sort stages to match ORDERED_NAMES by hiring_stage_id
    const orderedStagesForRender = ORDERED_NAMES.map(orderedItem => {
        const stage = (hiringStages || []).find(s => s.hiring_stage_id === orderedItem.id);
        if (!stage) return null;
        return { stage, orderedItem };
    }).filter(Boolean);

    if (activeSettingsView === 'Pre Screening') {
        return (
            <PreScreening jobId={jobId} setType={() => setActiveSettingsView(null)} />
        )
    }

    if (activeSettingsView === 'Voice Call') {
        return <TeleScreening jobId={jobId} setType={() => setActiveSettingsView(null)} />
    }

    const goToNextStage = async () => {
        if (onTransitionStart) {
            onTransitionStart("Creating automation, please wait..");
        }

        try {
            const resp = await dispatch(setupAutomations(jobId));
            if (resp && resp.success !== false) {
                if (onNext) onNext(null, true);
            } else {
                if (onTransitionError) onTransitionError();
            }
        } catch (error) {
            if (onTransitionError) onTransitionError();
        }
    }

    if (transitioning) {
        return (
            <div className="cj-stage2" style={{ pointerEvents: 'none' }}>
                <div className="cj-stage-header">
                    <div>
                        <p className="gradient-text-loading">{transitionMessage}</p>
                    </div>
                </div>
                <div className="cj-features-grid">
                    {[1, 2, 3, 4, 5].map((idx) => (
                        <div key={idx} className="gradient-border-wrap" style={{ marginBottom: '16px' }}>
                            <div className="cj-feature-card-empty-skeleton" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="cj-stage2">
            {lockedModal && (
                <div className="cj-locked-modal-overlay" onClick={() => setLockedModal(false)}>
                    <div className="cj-locked-modal" onClick={e => e.stopPropagation()}>
                        <button className="cj-locked-modal-close" onClick={() => setLockedModal(false)} aria-label="Close">&#x2715;</button>
                        <div className="cj-locked-modal-icon">&#128274;</div>
                        <h3 className="cj-locked-modal-title">Feature Not Available</h3>
                        <p className="cj-locked-modal-message">This feature is not available on your current plan. Please upgrade your plan to use this feature.</p>
                    </div>
                </div>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity="error">
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <div>
                <div className="cj-stage-header">
                    <div>
                        <button className="cj-back-link" onClick={onBack} disabled={loadingStages || transitioning || generatingPreScreening} style={{cursor: loadingStages || transitioning || generatingPreScreening ? 'not-allowed' : ''}}>
                            <FaChevronLeft /> Back to Job Details
                        </button>
                    </div>
                    <button className="cj-btn-primary" onClick={() => goToNextStage()} disabled={loadingStages || transitioning || generatingPreScreening} style={{cursor: loadingStages || transitioning || generatingPreScreening ? 'not-allowed' : ''}}>
                        Next: Setup automation <span className="cj-btn-arrow">→</span>
                    </button>
                </div>
                <div>
                    <h3 className="cj-stage-title">Select Screening Options</h3>
                    <p className="cj-stage-subtitle">
                        Gappeo has preselected the most used features. Customize them for your requirements
                    </p>
                </div>
            </div>

            <div className="cj-features-grid" style={{ pointerEvents: generatingPreScreening ? 'none' : 'auto' }}>
                {orderedStagesForRender.map(({ stage, orderedItem }) => {
                    const isPreScreening = orderedItem.name === 'Pre Screening';

                    if (isPreScreening && generatingPreScreening) {
                        return (
                            <div key={stage.hiring_stage_id} className="gradient-border-wrap" style={{maskComposite : 'unset', borderRadius:'12px' }}>
                                <div className="cj-feature-card" style={{ opacity: 0.7, cursor: generatingPreScreening ? 'not-allowed' : 'default', }}>
                                    <div className='cj-feature-card-left'>
                                        <span className="cj-feature-icon">
                                            <RiUserSharedLine size={40} />
                                        </span>
                                        <div className="cj-feature-info">
                                            <p className="cj-feature-label">Pre Screening</p>
                                            <p className="cj-feature-desc gradient-text-loading">Creating Pre Screening questions, please wait..</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    const mapData = FEATURE_MAPPING[orderedItem.name] || {
                        description: 'No description provided',
                        icon: <LuFileText size={40} />
                    };

                    const isSelected = !!selected[stage.hiring_stage_id];
                    const isLocked = stage.is_locked;
                    const remainingCredits = getRemainingCredits(orderedItem.name);

                    return (
                        <div
                            key={stage.hiring_stage_id}
                            className={`cj-feature-card ${isSelected ? 'cj-feature-card--selected' : ''} ${isLocked ? 'cj-feature-card--locked' : ''}`}
                            onClick={() => toggleFeature(stage)}
                            style={{ opacity: isLocked ? 0.6 : 1 }}
                        >
                            <div className='cj-feature-card-left'>
                                <span className={`cj-feature-icon ${isSelected ? 'cj-feature-icon--selected' : ''}`}>
                                    {mapData.icon}
                                    <span className={`cj-feature-select-label ${isSelected ? 'cj-feature-select-label--selected' : ''}`}>
                                        {isSelected ? 'Selected' : 'Select'}
                                    </span>
                                </span>
                                <div className="cj-feature-info d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="cj-feature-label" style={{ display: 'flex', alignItems: 'center', gap: '4px', }}>
                                            <span className='cj-feature-label-title'>{orderedItem.name}</span>
                                            {/* {remainingCredits !== null && (
                                                <span className='cj-feature-label-disc'>
                                                    {remainingCredits} Credits Available
                                                </span>
                                            )} */}
                                        </p>
                                        <p className="cj-feature-desc">{mapData.description}</p>
                                    </div>
                                    <div className="cj-feature-actions">
                                        {['Pre Screening', 'AI Telephonic Screening'].includes(orderedItem.name) && (
                                            <button
                                                className="cj-edit-btn"
                                                onClick={(e) => handleEditClick(e, stage.hiring_stage?.name || stage.name)}
                                                aria-label="Edit"
                                                style={{ display: 'flex', alignItems: 'end', }}
                                            >
                                                <img src="/images/square-pen-black.svg" title='Edit' alt="Edit" width={24} height={24} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    )
}

export default Stage2SelectAIFeatures
